import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Modal component with proper Portal rendering, scroll lock, focus trap, and accessibility
 */
const Modal = ({ 
  isOpen, 
  onClose, 
  children, 
  title,
  icon,
  maxWidth = 'max-w-md',
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  testId = 'modal'
}) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Handle ESC key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && closeOnEscape) {
      onClose();
    }
  }, [onClose, closeOnEscape]);

  // Focus trap - keep focus inside modal
  const handleFocusTrap = useCallback((e) => {
    if (!modalRef.current) return;
    
    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  }, []);

  // Lock body scroll and handle focus
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('keydown', handleFocusTrap);
      
      // Focus first focusable element
      setTimeout(() => {
        const focusable = modalRef.current?.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        focusable?.focus();
      }, 10);
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleFocusTrap);
      previousActiveElement.current?.focus();
    };
  }, [isOpen, handleKeyDown, handleFocusTrap]);

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? `${testId}-title` : undefined}
      data-testid={testId}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />
      
      {/* Modal Container */}
      <div 
        ref={modalRef}
        className={`relative w-full ${maxWidth} rounded-xl overflow-hidden flex flex-col`}
        style={{
          background: 'linear-gradient(180deg, #18181B 0%, #09090B 100%)',
          border: '1px solid rgba(124, 92, 255, 0.2)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 40px rgba(124, 92, 255, 0.1)',
          maxHeight: '85vh'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div 
            className="px-6 py-4 border-b flex items-center justify-between shrink-0"
            style={{ borderColor: 'rgba(255,255,255,0.1)' }}
          >
            <div className="flex items-center gap-3">
              {icon && (
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ 
                    background: 'linear-gradient(135deg, #7C5CFF 0%, #5B8CFF 100%)'
                  }}
                >
                  {icon}
                </div>
              )}
              {title && (
                <h2 
                  id={`${testId}-title`}
                  className="text-lg font-semibold" 
                  style={{ color: '#FAFAFA' }}
                >
                  {title}
                </h2>
              )}
            </div>
            {showCloseButton && (
              <button 
                onClick={onClose}
                className="p-2 rounded-lg transition-colors hover:bg-white/5"
                aria-label="Close modal"
                data-testid={`close-${testId}`}
              >
                <X className="w-5 h-5" style={{ color: '#71717A' }} />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto flex-1">
          {children}
        </div>
      </div>
    </div>
  );

  // Render to body via Portal
  return createPortal(modalContent, document.body);
};

export default Modal;
